/* Shared presentational helpers for Analysis Studio 2. Pure functions only. */
import { formatters } from 'services';
import type { TradeAttribute } from './live/types';
import {
  ANALYZED_METRICS,
  PTF_RETURN_ANALYZED,
  SHARPE_ANALYZED,
  ALPHA_ANALYZED,
  MAX_DD_ANALYZED,
  VAR_ANALYZED,
  ORDERS_ANALYZED,
  AVG_SLIPPAGE,
  AVG_ACCURACY,
  AVG_HOLDING_PERIOD,
  CURRENT_LEVERAGE,
  VOL_ANALYZED,
  AVG_WEIGHTED_ACCURACY,
  LIMIT_BREAKING_ANALYZED,
} from 'state';

/* ---- blotter trade attribute (label + antd Tag color) ------------------- */
export const ATTRIBUTE_META: Record<
  TradeAttribute,
  { label: string; color: string }
> = {
  initiation: { label: 'Initiation', color: 'blue' },
  building_up: { label: 'Building up', color: 'green' },
  trimming_down: { label: 'Trimming down', color: 'gold' },
  closing: { label: 'Closing', color: 'default' },
  reversal: { label: 'Reversal', color: 'purple' },
};

/* ---- market regime (label + antd Tag color), keyed by the LLM slug --------- */
export const REGIME_META: Record<string, { label: string; color: string }> = {
  uptrend: { label: 'Uptrend', color: 'green' },
  downtrend: { label: 'Downtrend', color: 'red' },
  range_bound: { label: 'Range-bound', color: 'default' },
  high_volatility: { label: 'High Volatility', color: 'gold' },
  breakout: { label: 'Breakout', color: 'blue' },
  reversal: { label: 'Reversal', color: 'purple' },
};

/* ---- band (A/B/C/D) ----------------------------------------------------- */
export const BAND_LABELS = ['A', 'B', 'C', 'D'];
export const BAND_COLORS = ['#0e9f6e', '#0f5fd6', '#d48806', '#d14343'];

export const bandLabel = (band: number | null): string | null =>
  band == null ? null : (BAND_LABELS[band] ?? null);
export const bandColor = (band: number | null): string =>
  band == null ? '#8c8c8c' : (BAND_COLORS[band] ?? '#8c8c8c');

/* ---- sign coloring ------------------------------------------------------ */
export const signClass = (v: number | undefined | null): string => {
  if (v == null || Number.isNaN(v)) return '';
  if (v > 0) return 's2-pos';
  if (v < 0) return 's2-neg';
  return 's2-muted';
};

/* ---- value formatters --------------------------------------------------- */
export const pct = (v: number | undefined | null) =>
  v == null ? '—' : formatters.percent1Digit(v);
export const pct2 = (v: number | undefined | null) =>
  v == null ? '—' : formatters.percent2Digit(v);
export const money = (v: number | undefined | null) =>
  v == null ? '—' : `${formatters.million(v)}`;
export const num = (v: number | undefined | null, digits = 2) =>
  v == null ? '—' : formatters.commas2Digits(Number(v.toFixed(digits)));
export const int = (v: number | undefined | null) =>
  v == null ? '—' : formatters.commasNoDigits(v);

/** Signed money with leading +/- and color-friendly string. */
export const signedMoney = (v: number) =>
  `${v >= 0 ? '+' : ''}${formatters.million(v)}`;

/* ---- analyzed-metric label + per-metric formatting ---------------------- */
export const analyzedLabel = (metric: number): string =>
  ANALYZED_METRICS[metric] ?? `Metric ${metric}`;

const PCT_METRICS = new Set<number>([
  PTF_RETURN_ANALYZED,
  MAX_DD_ANALYZED,
  VOL_ANALYZED,
  AVG_ACCURACY,
  AVG_WEIGHTED_ACCURACY,
]);

// Monetary / net-value metrics — backend sends raw amounts (often in the
// millions). Shown via the millions formatter, matching the real metrics tab.
// TEMP: VaR is treated as a monetary value here too; revisit when VaR units
// are finalised (W2).
const MONEY_METRICS = new Set<number>([VAR_ANALYZED, LIMIT_BREAKING_ANALYZED]);

export const formatAnalyzed = (metric: number, value: number): string => {
  if (MONEY_METRICS.has(metric)) return formatters.million(value);
  if (PCT_METRICS.has(metric)) return formatters.percent1Digit(value);
  if (metric === AVG_SLIPPAGE) return formatters.percent2Digit(value);
  if (metric === ORDERS_ANALYZED) return formatters.commasNoDigits(value);
  if (metric === CURRENT_LEVERAGE) return `${formatters.commas2Digits(value)}×`;
  if (metric === AVG_HOLDING_PERIOD)
    return `${formatters.commas1Digit(value)} days`;
  if (metric === SHARPE_ANALYZED || metric === ALPHA_ANALYZED)
    return formatters.commas2Digits(value);
  return formatters.commas2Digits(value);
};

/** True when higher values are worse for this metric (drives sign coloring). */
export const isRiskMetric = (metric: number): boolean =>
  metric === MAX_DD_ANALYZED ||
  metric === VAR_ANALYZED ||
  metric === VOL_ANALYZED ||
  metric === AVG_SLIPPAGE ||
  metric === CURRENT_LEVERAGE;

/* ---- Scenario-date relativisation (player / trainee historical view) -------
   In a HISTORICAL session a player must never see the real calendar dates of
   the replayed period (they could identify it and trade on hindsight). For the
   per-player view we show "Day N" — calendar days since the session start — in
   place of every absolute scenario date. The trainer/cohort view keeps real
   dates and passes NO relativiser. */

/** Day index of a scenario date relative to the session start (calendar days,
 *  1-based). Matches the studio charts' `i*freq+1` axis for day-stepped
 *  sessions. */
export const scenarioDayNumber = (iso: string, startISO: string): number =>
  Math.floor((Date.parse(iso) - Date.parse(startISO)) / 86_400_000) + 1;

/** Maps a scenario date to a player-safe relative label ("Day N"), or `null`
 *  when it cannot (missing anchor / unparseable) — a `null` return is the
 *  signal "fall back to your normal absolute format". The trainer/cohort view
 *  passes no relativiser at all, so every call site keeps its real date. */
export type RelativizeDate = (iso?: string | null) => string | null;

/** Builds a relativiser anchored on the session `start_date`. Used ONLY for the
 *  per-player (trainee) historical view; returns `null` for missing/unparseable
 *  inputs so callers degrade to their existing absolute formatting. */
export const makeRelativizer =
  (startISO?: string | null): RelativizeDate =>
  iso => {
    if (!iso || !startISO) return null;
    const n = scenarioDayNumber(iso, startISO);
    return Number.isFinite(n) ? `Day ${n}` : null;
  };
